import { useState } from 'react';
import { absurdPolls } from '@/mocks/ranking';
import { fmtCount } from '@/lib/format';
import SectionHeading from './SectionHeading';

export default function AbsurdSection() {
  const [extra, setExtra] = useState<Record<number, number>>({});
  const [active, setActive] = useState<Record<number, boolean>>({});
  const [flash, setFlash] = useState<Record<number, boolean>>({});

  const vote = (id: number) => {
    setExtra((e) => ({ ...e, [id]: (e[id] || 0) + 1 }));
    setActive((v) => ({ ...v, [id]: true }));
    setFlash((f) => ({ ...f, [id]: true }));
    window.setTimeout(() => setFlash((f) => ({ ...f, [id]: false })), 700);
  };

  return (
    <section id="absurd" className="scroll-mt-20 bg-background-50 py-14 md:py-20">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6 lg:px-10">
        <SectionHeading
          eyebrow="越离谱越上头 · 全网快乐源泉"
          title="离谱投票"
          desc="有些瓜严肃不起来，有些票投完就笑出声。给这些「离谱到想报警」的选择投一票，看看全网有多爱凑热闹。"
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {absurdPolls.map((poll) => {
            const total = poll.votes + (extra[poll.id] || 0);
            const deg = (poll.ratio / 100) * 360;
            return (
              <article key={poll.id} className="group flex flex-col overflow-hidden rounded-3xl border border-background-200 bg-background-100/60 sm:flex-row">
                <div className="relative h-44 shrink-0 overflow-hidden sm:h-auto sm:w-40 md:w-44">
                  <img
                    src={poll.cover}
                    alt={`离谱投票：${poll.question}`}
                    title={`吃瓜榜单离谱投票 · ${poll.question}`}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-full bg-background-50/95 px-2.5 py-1 text-xs font-semibold text-primary-700">
                    {poll.category}
                  </span>
                </div>

                <div className="flex flex-1 flex-col p-5">
                  <h3 className="text-base font-bold leading-snug text-foreground-950 md:text-lg">{poll.question}</h3>

                  {/* result + gauge */}
                  <div className="mt-4 flex items-center gap-4">
                    <div
                      className="relative flex h-16 w-16 shrink-0 items-center justify-center rounded-full"
                      style={{ background: `conic-gradient(oklch(var(--accent-500)) ${deg}deg, oklch(var(--background-200)) 0deg)` }}
                    >
                      <div className="flex h-[54px] w-[54px] flex-col items-center justify-center rounded-full bg-background-100">
                        <span className="font-display text-base leading-none text-accent-700">{poll.ratio}%</span>
                        <span className="mt-0.5 text-[10px] text-foreground-500">离谱指数</span>
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between text-xs text-foreground-500">
                        <span>全网离谱指数</span>
                        <span className="font-semibold text-accent-700">{poll.ratio}%</span>
                      </div>
                      <div className="mt-2 flex h-2 w-full overflow-hidden rounded-full bg-background-200">
                        <div className="bg-accent-500 transition-all duration-500" style={{ width: `${poll.ratio}%` }} />
                      </div>
                      <p className="mt-2 text-xs text-foreground-500">
                        <b className="text-foreground-700">{fmtCount(total)}</b> 位网友已「献出离谱一票」
                      </p>
                    </div>
                  </div>

                  <div className="mt-auto flex items-center justify-between gap-3 pt-4">
                    <button
                      onClick={() => vote(poll.id)}
                      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                        active[poll.id]
                          ? 'bg-accent-100 text-accent-800'
                          : 'bg-accent-500 text-background-50 hover:bg-accent-600'
                      }`}
                    >
                      <i className="ri-emotion-laugh-fill text-base leading-none" />
                      {active[poll.id] ? '已投 · 感谢整活' : '投出离谱一票'}
                    </button>
                    <span
                      className={`text-sm font-bold text-primary-600 transition-opacity ${flash[poll.id] ? 'opacity-100' : 'opacity-0'}`}
                    >
                      <i className="ri-thumb-up-fill mr-1 leading-none" />
                      感谢 +1
                    </span>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}